import React, { useRef, useContext, useState } from 'react'
import * as Sentry from '@sentry/browser'
import { CaptureContext } from '@sentry/types'
import styled from 'styled-components'

import { AppContext } from 'contexts/AppProvider'
import { Container, BtnBack, Button, Fonts, LabelBox, ScrollPage } from 'components/base-ui'
import { BtnBackWrapper, Space, StyledTitle, ButtonWrapperStickyBottom } from './styles'
import SecureOpnCreditCard from './SecureOpnCreditCard'
import { useTranslation } from 'react-i18next'
import { isDevelopment } from 'variables/environment'

import FormCreditCard, { CreditCard } from './FormCreditCard'
import useForm from 'hooks/useForm'
import useOmise from 'services/hooks/useOmise'

import { useAddCreditCardMutation, CreditCardType } from 'graphQL/hatostore/generate/operations'

import { cloneObject } from 'helpers'
import PageRemoveCreditCard from './PageRemoveCreditCard'

interface PageOverlayEditCreditCardProps {
  show: boolean
  onBack?: () => void
  creditCard: CreditCardType
  vendorID: number
  onAddedNewCreditCardCompleted?: (creditCard: CreditCardType) => void
  onRemoveCreditCardCompleted?: (creditCard: CreditCardType) => void
}

const initialFormData: CreditCard = {
  cardNumber: '',
  cardHolderName: '',
  expireDate: '',
  cvc: '',
}

const PageOverlayEditCreditCard: React.FC<PageOverlayEditCreditCardProps> = ({
  show,
  onBack,
  creditCard,
  vendorID,
  onAddedNewCreditCardCompleted,
  onRemoveCreditCardCompleted,
}) => {
  const { accessToken, brand } = useContext(AppContext)
  const { t } = useTranslation()
  const refWrapper = useRef(null)

  const [isRequestAddNewCard, setIsRequestAddNewCard] = useState<boolean>(false)
  const [errorMsgCreditCard, setErrorMsgCreditCard] = useState<string>('')
  const [isFocusForm, setIsFocusForm] = useState(false)
  const { data: formCreditData, handleOnChange, setAsyncInitData } = useForm<CreditCard>(initialFormData)
  const refForm = useRef<any>(null)
  const refCardHolderName = useRef(null)
  const refCardNumber = useRef(null)
  const refExpireDate = useRef(null)
  const refCVCInput = useRef(null)
  const [creditCardBrand, setCreditCardBrand] = useState('')

  const omise = useOmise({
    omisePublicKey: brand?.omisePublicKey ? brand.omisePublicKey : process.env.REACT_APP_OMISE_PUBLIC_KEY,
  })
  const omiseCreateToken = omise.createToken

  const [addCreditCard] = useAddCreditCardMutation({
    context: {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  })

  return (
    <ScrollPage id="edit-credit-card-overlay" ref={refWrapper}>
      <Container style={{ paddingTop: '88px', position: 'relative' }}>
        <BtnBackWrapper>
          <Container>
            <BtnBack
              onClick={() => {
                onBack && onBack()
              }}
              enableFastClick={false}
            />
          </Container>
        </BtnBackWrapper>
      </Container>

      <ContainerDetail id="detail-container">
        {creditCard ? (
          <PageRemoveCreditCard
            creditCard={creditCard}
            vendorID={vendorID}
            onBack={onBack}
            onRemoveCreditCardCompleted={onRemoveCreditCardCompleted}
          />
        ) : (
          <>
            <div>
              <StyledTitle>{t('paymentMethodPage.creditCard.create')}</StyledTitle>
              <FormCreditCard
                formID="form-new-credit-card"
                formCreditData={formCreditData}
                handleOnChange={handleOnChange}
                setAsyncInitData={setAsyncInitData}
                refForm={refForm}
                refCardNumber={refCardNumber}
                refExpireDate={refExpireDate}
                refCVCInput={refCVCInput}
                refCardHolderName={refCardHolderName}
                errorMsgCreditCard={errorMsgCreditCard}
                onUpdatedForm={() => {
                  setErrorMsgCreditCard('')
                }}
                creditCardBrand={creditCardBrand}
                onDetectBrandCreditCard={(brand) => setCreditCardBrand(brand)}
                onFocus={() => setIsFocusForm(true)}
                onBlur={() => setIsFocusForm(false)}
              />
            </div>
            <StyledLabelBox
              type="info"
              text={<Fonts.SmallTitleBold>{t('paymentMethodPage.secureCreditCardPayment.info')}</Fonts.SmallTitleBold>}
            />
            <Space isShow={!isFocusForm} />
            <ButtonWrapperStickyBottom>
              <SecureOpnCreditCard />
              <Button
                md
                onClick={handleAddCreditCard}
                isProcessing={isRequestAddNewCard}
                disabled={isRequestAddNewCard}
              >
                {t('paymentMethodPage.creditCard.save')}
              </Button>
            </ButtonWrapperStickyBottom>
          </>
        )}
      </ContainerDetail>
    </ScrollPage>
  )

  function handleShowSpotErrorCreditCard(errorCode) {
    if (errorCode === 'invalid_security_code') {
      refForm.current.showError(refCVCInput.current, 'Security Code is invalid')
    }

    if (/number is invalid/.test(errorCode)) {
      refForm.current.showError(refCardNumber.current, 'Card Number is invalid')
    }

    if (/expiration date/.test(errorCode)) {
      refForm.current.showError(refExpireDate.current, 'Expire Date is invalid')
    }
  }

  async function handleAddCreditCard() {
    try {
      const { cardHolderName, cardNumber, expireDate, cvc } = formCreditData

      const expireDateSplit = expireDate.split('/')
      const expiration_month = expireDateSplit[0]
      const expiration_year = expireDateSplit[1]

      const card = {
        name: cardHolderName,
        number: cardNumber,
        expiration_month: expiration_month,
        expiration_year: expiration_year,
        security_code: cvc,
      }

      setIsRequestAddNewCard(true)
      omiseCreateToken({
        card,
        onSuccess: async ({ token }) => {
          const result = await addCreditCard({
            variables: {
              vendorID,
              token,
            },
          })

          setIsRequestAddNewCard(false)
          if (result?.data?.addCreditCard) {
            onBack && onBack()
            onAddedNewCreditCardCompleted && onAddedNewCreditCardCompleted(result?.data?.addCreditCard)
            setTimeout(() => {
              const _initialFormData = cloneObject(initialFormData)
              // @ts-ignore
              setAsyncInitData(_initialFormData)
              resetFormState()
            }, 400)
          }
        },
        // @ts-ignore
        onFailure: ({ errorMessage, errorMessageFromOmise, errorCode }) => {
          setIsRequestAddNewCard(false)
          handleShowSpotErrorCreditCard(errorMessageFromOmise)
          if (errorMessage) {
            setErrorMsgCreditCard(errorMessage)
            !isDevelopment &&
              Sentry.captureException(new Error('HatoStore error: Create Omise Token when Add New CreditCard'), {
                error: errorMessage,
              } as CaptureContext)
          } else {
            setErrorMsgCreditCard(errorMessageFromOmise)
          }
        },
      })
    } catch (err) {}
  }

  function resetFormState() {
    setCreditCardBrand('')
    if (refForm.current) {
      refForm.current.hideError?.(refCardNumber.current)
      refForm.current.hideError?.(refCardHolderName.current)
      refForm.current.hideError?.(refExpireDate.current)
      refForm.current.hideError?.(refCVCInput.current)
    }
  }
}

export default PageOverlayEditCreditCard

const ContainerDetail = styled(Container)`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`

const StyledLabelBox = styled(LabelBox)`
  width: 100%;
  padding: 10px;
  margin-bottom: 20px;
  border: 0;
`
